import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import DealerNav from "./DealerNav";
import { getVehicleCanonicalUrl, getVehiclePath } from "../Utils/vehicleUrls";

const API = process.env.REACT_APP_API_BASE; // e.g. https://api.meshum.com

// --- 3-dot actions menu used per row ---
function InventoryRowActions({ vehicle, publicUrl, isSold, headers, onReload }) {
  const id = vehicle?.id;
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const btnRef = useRef(null);
  const menuRef = useRef(null);
  const navigate = useNavigate();

  // close on outside click / Esc
  useEffect(() => {
    const onDoc = (e) => {
      if (!menuRef.current || !btnRef.current) return;
      if (menuRef.current.contains(e.target) || btnRef.current.contains(e.target)) return;
      setOpen(false);
    };
    const onEsc = (e) => e.key === "Escape" && setOpen(false);
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onEsc);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onEsc);
    };
  }, []);

  const viewPublic = () => {
    const url = publicUrl?.startsWith("http") ? publicUrl : publicUrl || getVehiclePath(vehicle);
    window.open(url, "_blank", "noopener,noreferrer");
  };

  const copyLink = async () => {
    const url = publicUrl?.startsWith("http")
      ? publicUrl
      : publicUrl
        ? `${window.location.origin}${publicUrl}`
        : getVehicleCanonicalUrl(vehicle, window.location.origin);
    try { await navigator.clipboard.writeText(url); } catch {}
    setOpen(false);
  };

  const toggleSold = async () => {
    if (!headers) return;
    try {
      setBusy(true);
      const path = isSold ? "mark-active" : "mark-sold";
      const res = await fetch(`${API}/dealer/inventory/${id}/${path}`, {
        method: "POST",
        headers,
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      onReload?.();
      setOpen(false);
    } catch (e) {
      alert("Unable to update sold status.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="relative inline-block text-left">
      <button
        ref={btnRef}
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="inline-flex items-center justify-center h-9 w-9 rounded-lg border hover:bg-gray-50"
        style={{ background: "#fff", borderColor: "#D1D5DB" }}
        aria-haspopup="menu"
        aria-expanded={open}
        title="Actions"
      >
        {/* 3 horizontal dots (CSS, not an icon) */}
        <div className="flex flex-row items-center justify-center gap-1">
          <span style={{ width: 6, height: 6, borderRadius: "50%", background: "#374151", display: "block" }} />
          <span style={{ width: 6, height: 6, borderRadius: "50%", background: "#374151", display: "block" }} />
          <span style={{ width: 6, height: 6, borderRadius: "50%", background: "#374151", display: "block" }} />
        </div>
      </button>

      {open && (
        <div
          ref={menuRef}
          role="menu"
          className="absolute right-0 z-50 mt-2 w-48 origin-top-right rounded-lg shadow-lg overflow-hidden"
          style={{ background: "#fff", color: "#1F2937", border: "1px solid #E5E7EB" }}
        >
          <button
            type="button"
            role="menuitem"
            className="w-full px-3 py-2 text-left text-sm"
            style={{ background: "#fff", color: "#1F2937", borderBottom: "1px solid #E5E7EB" }}
            onClick={() => { setOpen(false); navigate(`/dealer/inventory/${id}/edit`); }}
            onMouseEnter={(e) => e.currentTarget.style.background = "#F9FAFB"}
            onMouseLeave={(e) => e.currentTarget.style.background = "#fff"}
          >
            Edit
          </button>

          <button
            type="button"
            role="menuitem"
            className="w-full px-3 py-2 text-left text-sm"
            style={{ background: "#fff", color: "#1F2937", borderBottom: "1px solid #E5E7EB" }}
            onClick={() => { setOpen(false); viewPublic(); }}
            onMouseEnter={(e) => e.currentTarget.style.background = "#F9FAFB"}
            onMouseLeave={(e) => e.currentTarget.style.background = "#fff"}
          >
            View (public)
          </button>

          <button
            type="button"
            role="menuitem"
            className="w-full px-3 py-2 text-left text-sm"
            style={{ background: "#fff", color: "#1F2937", borderBottom: "1px solid #E5E7EB" }}
            onClick={copyLink}
            onMouseEnter={(e) => e.currentTarget.style.background = "#F9FAFB"}
            onMouseLeave={(e) => e.currentTarget.style.background = "#fff"}
          >
            Copy link
          </button>

          <button
            type="button"
            role="menuitem"
            disabled={busy}
            className="w-full px-3 py-2 text-left text-sm disabled:opacity-60"
            style={{ background: "#fff", color: "#1F2937" }}
            onClick={toggleSold}
            onMouseEnter={(e) => e.currentTarget.style.background = "#F9FAFB"}
            onMouseLeave={(e) => e.currentTarget.style.background = "#fff"}
          >
            {isSold ? "Mark as Active" : "Mark as Sold"}
          </button>
        </div>
      )}
    </div>
  );
}

export default function DealerInventoryPortal() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = useMemo(() => localStorage.getItem("dealerToken") || "", []);
  const authedHeaders = useMemo(
    () => ({ Authorization: `Bearer ${token}`, "Content-Type": "application/json" }),
    [token]
  );

  // Redirect if not logged in
  useEffect(() => {
    if (!token) navigate("/dealer/login", { replace: true });
  }, [token, navigate]);

  // Filters / state
  const [view, setView] = useState(() => ["all", "active", "sold"].includes(searchParams.get("view")) ? searchParams.get("view") : "all");
  const [q, setQ] = useState("");                     // keyword (make/model/trim/VIN)
  const [minYear, setMinYear] = useState("");
  const [maxYear, setMaxYear] = useState("");
  const [sort, setSort] = useState("recent");         // recent | price_asc|price_desc | year_desc | mileage_asc | dom_asc|dom_desc | contacts_desc
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(24);

  // NEW: Quality filters
  const [noPhoto, setNoPhoto] = useState(false);
  const [noPrice, setNoPrice] = useState(false);
  const [highDom, setHighDom] = useState(""); // string input; send if > 0
  const [position, setPosition] = useState(() => ["below", "near", "above"].includes(searchParams.get("position")) ? searchParams.get("position") : "all");

  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  const load = async () => {
    setLoading(true);
    setErr("");
    try {
      const qs = new URLSearchParams();
      qs.set("page", String(page));
      qs.set("pageSize", String(pageSize));
      if (view) qs.set("view", view);
      if (sort) qs.set("sort", sort);
      if (q.trim()) qs.set("q", q.trim());
      if (minYear) qs.set("minYear", minYear);
      if (maxYear) qs.set("maxYear", maxYear);

      // NEW: quality params
      if (noPhoto) qs.set("noPhoto", "true");
      if (noPrice) qs.set("noPrice", "true");
      if (highDom && Number(highDom) > 0) qs.set("highDom", String(Number(highDom)));
      if (position !== "all") qs.set("position", position);

      const res = await fetch(`${API}/dealer/inventory?${qs.toString()}`, {
        headers: authedHeaders,
      });
      if (res.status === 401) {
        navigate("/dealer/login", { replace: true });
        return;
      }
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setRows(Array.isArray(data.rows) ? data.rows : []);
      setTotal(Number(data.total || 0));
    } catch (e) {
      setErr("Could not load inventory.");
      setRows([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  };

  // Initial + whenever filters change
  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view, q, minYear, maxYear, sort, page, pageSize, noPhoto, noPrice, highDom, position]);

  const totalPages = Math.max(1, Math.ceil((total || 0) / pageSize));

  const apply = () => {
    setPage(1);
    load();
  };

  const clearAll = () => {
    setView("all");
    setQ("");
    setMinYear("");
    setMaxYear("");
    setSort("recent");
    setPageSize(24);
    setPage(1);
    // reset quality
    setNoPhoto(false);
    setNoPrice(false);
    setHighDom("");
    setPosition("all");
  };

  return (
    <div className="max-w-6xl mx-auto p-4">
      <DealerNav />

      <div className="flex flex-wrap items-end gap-3 mb-4">
        <div>
          <label className="block text-xs text-gray-600 mb-1">View</label>
          <select
            value={view}
            onChange={(e) => { setView(e.target.value); setPage(1); }}
            className="h-10 px-3 rounded-lg border border-gray-300 text-sm bg-white"
          >
            <option value="all">All</option>
            <option value="active">Active</option>
            <option value="sold">Sold</option>
          </select>
        </div>

        <div>
          <label className="block text-xs text-gray-600 mb-1">Position</label>
          <select
            value={position}
            onChange={(e) => { setPosition(e.target.value); setPage(1); }}
            className="h-10 px-3 rounded-lg border border-gray-300 text-sm bg-white"
          >
            <option value="all">All</option>
            <option value="below">Below</option>
            <option value="near">Near</option>
            <option value="above">Above</option>
          </select>
        </div>

        <div>
          <label className="block text-xs text-gray-600 mb-1">Search (make/model/trim/VIN)</label>
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && apply()}
            placeholder="e.g. camry or 1HGCM..."
            className="h-10 w-64 px-3 rounded-lg border border-gray-300 text-sm"
          />
        </div>

        <div>
          <label className="block text-xs text-gray-600 mb-1">Min Year</label>
          <input
            type="number"
            value={minYear}
            onChange={(e) => setMinYear(e.target.value)}
            className="h-10 w-24 px-3 rounded-lg border border-gray-300 text-sm"
          />
        </div>

        <div>
          <label className="block text-xs text-gray-600 mb-1">Max Year</label>
          <input
            type="number"
            value={maxYear}
            onChange={(e) => setMaxYear(e.target.value)}
            className="h-10 w-24 px-3 rounded-lg border border-gray-300 text-sm"
          />
        </div>

        <div>
          <label className="block text-xs text-gray-600 mb-1">Sort</label>
          <select
            value={sort}
            onChange={(e) => { setSort(e.target.value); setPage(1); }}
            className="h-10 px-3 rounded-lg border border-gray-300 text-sm bg-white"
          >
            <option value="recent">Most Recent</option>
            <option value="price_asc">Price ↑</option>
            <option value="price_desc">Price ↓</option>
            <option value="year_desc">Year ↓</option>
            <option value="mileage_asc">Mileage ↑</option>
            <option value="dom_desc">Days on Market ↓</option>
            <option value="dom_asc">Days on Market ↑</option>
            <option value="contacts_desc">Contacts ↓</option>
          </select>
        </div>

        <div>
          <label className="block text-xs text-gray-600 mb-1">Per Page</label>
          <select
            value={pageSize}
            onChange={(e) => { setPageSize(Number(e.target.value)); setPage(1); }}
            className="h-10 px-3 rounded-lg border border-gray-300 text-sm bg-white"
          >
            {[12, 24, 36, 48, 60, 100].map((sz) => (
              <option key={sz} value={sz}>{sz}</option>
            ))}
          </select>
        </div>

        {/* NEW: Quality filters */}
        <div className="flex items-center gap-4 ml-0 sm:ml-4">
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={noPhoto}
              onChange={(e) => { setNoPhoto(e.target.checked); setPage(1); }}
            />
            No Photo
          </label>

          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={noPrice}
              onChange={(e) => { setNoPrice(e.target.checked); setPage(1); }}
            />
            No Price
          </label>

          <div>
            <label className="block text-xs text-gray-600 mb-1">High DOM ≥</label>
            <input
              type="number"
              min="1"
              placeholder="60"
              value={highDom}
              onChange={(e) => setHighDom(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && (setPage(1), apply())}
              className="h-10 w-24 px-3 rounded-lg border border-gray-300 text-sm"
            />
          </div>
        </div>

        <div className="flex gap-2 ml-auto">
          <button
            onClick={apply}
            className="h-10 px-4 rounded-lg bg-blue-600 text-white hover:bg-blue-700"
          >
            Apply
          </button>
          <button
            onClick={clearAll}
            className="px-3 py-2 text-sm rounded border border-gray-300 bg-white text-gray-700 hover:bg-gray-50"
          >
            Clear
          </button>
        </div>
      </div>

      {err && (
        <div className="mb-3 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
          {err}
        </div>
      )}

      {/* Table */}
      <div className="bg-white border rounded-xl overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-gray-100">
            <tr>
              <th className="p-3 text-left">Vehicle</th>
              <th className="p-3 text-left">VIN</th>
              <th className="p-3 text-right">Price</th>
              <th className="p-3 text-right">Mestimate</th>
              <th className="p-3 text-right">Position</th>
              <th className="p-3 text-right">Mileage</th>
              <th className="p-3 text-center">DOM</th>
              <th className="p-3 text-center">Contacts</th>
              <th className="p-3 text-center">Listed</th>
              <th className="p-3 text-center">Sold</th>
              <th className="p-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody className="[&>tr:nth-child(odd)]:bg-white [&>tr:nth-child(even)]:bg-gray-50">
            {loading ? (
              <tr>
                <td colSpan={11} className="p-6 text-center text-gray-500">Loading…</td>
              </tr>
            ) : rows.length === 0 ? (
              <tr>
                <td colSpan={11} className="p-6 text-center text-gray-500">No vehicles found.</td>
              </tr>
            ) : (
              rows.map((r) => (
                <tr key={r.id} className="border-t">
                  <td className="p-3">
                    <div className="flex items-center gap-3">
                      <img
                        src={r.imageUrl}
                        alt={`${r.year} ${r.make} ${r.model}`}
                        className="w-16 h-12 object-cover rounded border"
                      />
                      <div>
                        <div className="font-medium">
                          {r.year} {r.make} {r.model} {r.trim}
                        </div>
                        {r.listingUrl && (
                          <a
                            className="text-blue-600 text-xs underline"
                            href={r.listingUrl}
                            target="_blank"
                            rel="noreferrer"
                          >
                            Public listing
                          </a>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="p-3">{r.vin || "—"}</td>
                  <td className="p-3 text-right">{r.price != null ? `$${Number(r.price).toLocaleString()}` : "—"}</td>
                  <td className="p-3 text-right">{r.mestimate != null ? `$${Number(r.mestimate).toLocaleString()}` : "—"}</td>
                  <td className="p-3 text-right">{formatPosition(r)}</td>
                  <td className="p-3 text-right">{r.mileage != null ? Number(r.mileage).toLocaleString() : "—"}</td>
                  <td className="p-3 text-center">
                    <span className={`px-2 py-0.5 rounded-full text-xs border ${
                      r.dom >= 60
                        ? "bg-red-50 text-red-700 border-red-200"
                        : r.dom >= 30
                        ? "bg-yellow-50 text-yellow-700 border-yellow-200"
                        : "bg-green-50 text-green-700 border-green-200"
                    }`}>
                      {r.dom ?? "—"}d
                    </span>
                  </td>
                  <td className="p-3 text-center">{r.contacts ?? 0}</td>
                  <td className="p-3 text-center">
                    {r.listedAt ? new Date(r.listedAt).toLocaleDateString() : "—"}
                  </td>
                  <td className="p-3 text-center">
                    {r.soldAt ? new Date(r.soldAt).toLocaleDateString() : "—"}
                  </td>
                  <td className="p-3 text-right">
                    <InventoryRowActions
                      vehicle={r}
                      publicUrl={r.listingUrl || getVehiclePath(r)}
                      isSold={!!r.soldAt}
                      headers={authedHeaders}
                      onReload={load}
                    />
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {!loading && (
        <div className="flex items-center justify-between gap-3 mt-3 text-sm">
          <div className="text-gray-600">
            Showing <b>{rows.length ? (page - 1) * pageSize + 1 : 0}</b>–<b>{Math.min(page * pageSize, total)}</b> of <b>{total}</b>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page <= 1}
              className="px-3 py-2 text-sm rounded-lg border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 disabled:opacity-50"
            >
              Prev
            </button>
            <span>Page <b>{page}</b> / {totalPages}</span>
            <button
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page >= totalPages}
              className="px-3 py-2 text-sm rounded-lg border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 disabled:opacity-50"
            >
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export function formatPosition(vehicle) {
  if (!vehicle?.position) return "—";
  if (vehicle.position === "Near") return "Near estimate";
  const amount = Math.abs(Number(vehicle.positionDifferenceAmount));
  if (!Number.isFinite(amount)) return "—";
  return `$${Math.round(amount).toLocaleString()} ${vehicle.position === "Below" ? "below" : "above"}`;
}
